import classnames from "classnames";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Badge, Collapse, Table } from "reactstrap";
import {
  collectObsoleteBomLines,
  getObsoleteLineKey,
  isOriginalObsoleteAlternate,
  prependOriginalObsoleteAlternate,
  OBSOLETE_ALT_SELECTED_ROW_CLASS,
} from "../../../constants/digiKeyColumns";
import AlternateRecommendationBadge from "../../AlternateRecommendationBadge";
import { ModalBox, ModalOverlay } from "../../Modals/ModalStyled";
import { Button } from "../../Styled";
import { DIGIKEY_AI_ALTERNATES } from "../../../constants/urls";

const formatPrice = (alternate) => {
  if (alternate.unit_price_display) {
    return alternate.unit_price_display;
  }
  if (alternate.unit_price != null && alternate.unit_price !== "") {
    const raw = String(alternate.unit_price).replace(/[^0-9.+-Ee]/g, "");
    const num = Number(raw);
    if (Number.isFinite(num)) {
      return `$${num.toFixed(5)}`;
    }
  }
  return "N/A";
};

const substitutesFromLine = (line) =>
  Array.isArray(line?.substitutes) ? line.substitutes : [];

// "2h ago", "45m ago", "just now" — for cache freshness labels.
const relativeTime = (isoStr) => {
  if (!isoStr) return "";
  const diffSec = Math.max(0, Math.floor((Date.now() - new Date(isoStr).getTime()) / 1000));
  if (diffSec < 60) return "just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return `${Math.floor(diffHr / 24)}d ago`;
};

const lineMpn = (line) =>
  String(
    line?.obsolete_part_number || line?.manufacturer_part_number || "",
  ).trim();

// Rows we got from Suggest Alternates
const isAiAlternate = (item) => {
  const group = String(item?.substitute_group || item?.source || "").toLowerCase();
  if (group === "ai") {
    return true;
  }
  return String(item?.substitute_type || "").toLowerCase().indexOf("ai") !== -1;
};

const ExternalLinkIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "inline-block", verticalAlign: "middle" }}
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const substituteMpn = (alternate) =>
  String(
    alternate?.manufacturer_part_number ||
      alternate?.manufacturerPartNumber ||
      "",
  ).trim();

const sortByUnitPrice = (items) =>
  [...(items || [])].sort((a, b) => {
    const priceA = a.unit_price != null && a.unit_price !== "" ? Number(a.unit_price) : Infinity;
    const priceB = b.unit_price != null && b.unit_price !== "" ? Number(b.unit_price) : Infinity;
    const safeA = Number.isFinite(priceA) ? priceA : Infinity;
    const safeB = Number.isFinite(priceB) ? priceB : Infinity;
    return safeA - safeB;
  });

// Pin the in-stock obsolete original at the top; remaining rows stay cheapest-first.
const sortAlternatesForDisplay = (items) => {
  const sorted = sortByUnitPrice(items);
  const originals = sorted.filter(isOriginalObsoleteAlternate);
  const rest = sorted.filter((item) => !isOriginalObsoleteAlternate(item));
  return [...originals, ...rest];
};

const aiNotes = (alternate) =>
  String(
    alternate?.notes ||
      alternate?.recommendation_text ||
      alternate?.description ||
      "",
  ).trim();

const formatPackage = (pkg) => {
  const text = String(pkg || "").trim();
  if (!text) return "N/A";
  const match = text.match(/^(.+?)\s*\((.+)\)$/);
  if (!match) return text;
  return (
    <>
      {match[1]}
      <div className="text-muted" style={{ fontSize: "0.75rem" }}>
        {match[2]}
      </div>
    </>
  );
};

const groupRejectedMpns = (rejected) => {
  const groups = {};
  (Array.isArray(rejected) ? rejected : []).forEach((item) => {
    const mpn = String(item?.mpn || "").trim();
    if (!mpn) {
      return;
    }
    const reason = String(item?.reason || "Not found on DigiKey").trim()
      || "Not found on DigiKey";
    if (!groups[reason]) {
      groups[reason] = [];
    }
    groups[reason].push(mpn);
  });
  return groups;
};

const AiRejectedReasons = ({ rejected }) => {
  const groups = groupRejectedMpns(rejected);
  const reasons = Object.keys(groups);
  if (!reasons.length) {
    return null;
  }

  return (
    <div className="mt-2" style={{ fontSize: "0.8rem", color: "#495057" }}>
      {reasons.map((reason) => (
        <div key={reason} className="mb-1">
          <strong>{reason}:</strong> {groups[reason].join(", ")}
        </div>
      ))}
    </div>
  );
};

const mergeAiRowIdentity = (fromLine, fromCache) => {
  if (!fromLine) {
    return fromCache;
  }
  if (!fromCache) {
    return fromLine;
  }
  const next = { ...fromCache, ...fromLine };
  // Keep DigiKey-verified fields from the latest API fetch when the BOM row is stale.
  [
    "unit_price",
    "unit_price_display",
    "digikey_part_number",
    "quantity_available",
    "part_detail_url",
    "part_status",
    "lifecycle_status",
    "digikey_verified",
    "digikey_verified_at",
    "digikey_available",
  ].forEach((key) => {
    const lineVal = next[key];
    const cacheVal = fromCache[key];
    if (
      (lineVal == null || lineVal === "") &&
      cacheVal != null &&
      cacheVal !== ""
    ) {
      next[key] = cacheVal;
    }
  });
  if (!next.source && fromCache.source) {
    next.source = fromCache.source;
  }
  if (!next.substitute_group && fromCache.substitute_group) {
    next.substitute_group = fromCache.substitute_group;
  }
  if (!next.substitute_type && fromCache.substitute_type) {
    next.substitute_type = fromCache.substitute_type;
  }
  if (!next.notes && fromCache.notes) {
    next.notes = fromCache.notes;
  }
  if (!next.recommendation_label && fromCache.recommendation_label) {
    next.recommendation_label = fromCache.recommendation_label;
  }
  if (!next.recommendation_text && fromCache.recommendation_text) {
    next.recommendation_text = fromCache.recommendation_text;
  }
  return next;
};

const overlaySelectedDigiKeyFields = (line, rows) => {
  const selectedMpn = String(line?.selected_alternate_mpn || "")
    .trim()
    .toUpperCase();
  if (!selectedMpn || !Array.isArray(rows) || !rows.length) {
    return rows || [];
  }
  const lineDkPn = String(line?.digikey_part_number || "").trim();
  const linePrice =
    line?.selected_alternate_unit_price != null
      ? line.selected_alternate_unit_price
      : line?.unit_price;
  if (!lineDkPn && linePrice == null) {
    return rows;
  }
  return rows.map((item) => {
    if (substituteMpn(item).toUpperCase() !== selectedMpn) {
      return item;
    }
    const next = { ...item };
    if (!String(next.digikey_part_number || "").trim() && lineDkPn) {
      next.digikey_part_number = lineDkPn;
    }
    if (
      (next.unit_price == null || next.unit_price === "") &&
      linePrice != null &&
      linePrice !== ""
    ) {
      next.unit_price = linePrice;
    }
    return next;
  });
};

const ObsoleteAlternateTable = ({
  substitutes,
  selectedMpn,
  onSelect,
  radioName,
}) => {
  // AI rows use extra Match/Notes columns; every source is cheapest-first, original on top.
  const hasAiRows = (substitutes || []).some(isAiAlternate);
  const items = sortAlternatesForDisplay(substitutes);

  if (!items.length) {
    return <p className="text-muted mb-0">No DigiKey alternates were found.</p>;
  }

  return (
    <>
    <div className="obsolete-alt-table-scroll">
      <Table className="table-bordered table-sm mb-0 parts-review-table obsolete-alt-table">
        <thead>
          {hasAiRows ? (
            <tr>
              <th style={{ width: "32px" }} />
              <th style={{ width: "10%" }}>Type</th>
              <th style={{ width: "16%" }}>MPN</th>
              <th style={{ width: "12%" }}>Manufacturer</th>
              <th style={{ width: "11%" }}>Package</th>
              <th style={{ width: "14%" }}>DigiKey PN</th>
              <th style={{ width: "10%" }}>Unit Price</th>
              <th style={{ width: "10%" }}>Match</th>
              <th>Notes</th>
            </tr>
          ) : (
            <tr>
              <th style={{ width: "32px" }} />
              <th style={{ width: "9%" }}>Type</th>
              <th style={{ width: "28%" }}>MPN</th>
              <th>Manufacturer</th>
              <th>DigiKey PN</th>
              <th style={{ width: "12%" }}>Unit Price</th>
              <th style={{ width: "14%" }}>Package</th>
            </tr>
          )}
        </thead>
        <tbody>
          {items.map((alternate, index) => {
            const mpn = substituteMpn(alternate);
            const isSelected = selectedMpn === mpn;

            return (
              <tr
                key={`${mpn}-${index}`}
                className={classnames({
                  [OBSOLETE_ALT_SELECTED_ROW_CLASS]: isSelected,
                })}
                data-selected-alternate={isSelected ? "true" : undefined}
                onClick={() => onSelect(mpn)}
                style={{ cursor: "pointer" }}
              >
                <td>
                  <input
                    type="radio"
                    name={radioName}
                    checked={isSelected}
                    onMouseDown={(e) => e.preventDefault()}
                    onChange={() => onSelect(mpn)}
                  />
                </td>
                {hasAiRows ? (
                  <>
                    <td className="obsolete-alt-ai-type">
                      {isOriginalObsoleteAlternate(alternate) ? (
                        <AlternateRecommendationBadge
                          label={alternate.recommendation_label}
                          text={alternate.recommendation_text}
                        />
                      ) : (
                        alternate.substitute_type || "AI Suggested"
                      )}
                    </td>
                    <td className="obsolete-alt-mpn-td">{mpn}</td>
                    <td className="obsolete-alt-ai-mfr">
                      {alternate.manufacturer || "N/A"}
                    </td>
                    <td className="obsolete-alt-ai-pkg">
                      {formatPackage(alternate.package)}
                    </td>
                    <td>
                      {alternate.digikey_part_number ? (
                        alternate.digikey_part_number
                      ) : alternate.digikey_available ? (
                        // Confirmed to exist on DigiKey; the PN itself resolves on save.
                        <span
                          style={{ color: "#198754", fontSize: "0.78rem" }}
                          title="Confirmed available on DigiKey. Part number and pricing are fetched when you save."
                        >
                          ✓ In catalog
                        </span>
                      ) : (
                        "N/A"
                      )}
                      {alternate.part_detail_url ? (
                        <a
                          href={alternate.part_detail_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="View on DigiKey"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            marginLeft: "6px",
                            color: "#cc0000",
                            opacity: 0.75,
                            lineHeight: 1,
                          }}
                        >
                          <ExternalLinkIcon />
                        </a>
                      ) : null}
                    </td>
                    <td>{formatPrice(alternate)}</td>
                    <td className="obsolete-alt-ai-match">
                      {isOriginalObsoleteAlternate(alternate) ? null : (
                        <AlternateRecommendationBadge
                          label={alternate.recommendation_label}
                        />
                      )}
                    </td>
                    <td className="obsolete-alt-ai-notes">
                      {aiNotes(alternate) || "—"}
                    </td>
                  </>
                ) : (
                  <>
                    <td>
                      {isOriginalObsoleteAlternate(alternate) ? (
                        <AlternateRecommendationBadge
                          label={alternate.recommendation_label}
                          text={alternate.recommendation_text}
                        />
                      ) : (
                        alternate.substitute_type || "N/A"
                      )}
                    </td>
                    <td className="obsolete-alt-mpn-td">
                      {mpn}
                      {isOriginalObsoleteAlternate(alternate) ? null : (
                        <AlternateRecommendationBadge
                          label={alternate.recommendation_label}
                          text={alternate.recommendation_text}
                        />
                      )}
                    </td>
                    <td>{alternate.manufacturer || "N/A"}</td>
                    <td>
                      {alternate.digikey_part_number || "N/A"}
                      {alternate.part_detail_url ? (
                        <a
                          href={alternate.part_detail_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="View on DigiKey"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            marginLeft: "6px",
                            color: "#cc0000",
                            opacity: 0.75,
                            lineHeight: 1,
                          }}
                        >
                          <ExternalLinkIcon />
                        </a>
                      ) : null}
                    </td>
                    <td>{formatPrice(alternate)}</td>
                    <td>{alternate.package || "N/A"}</td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
    {hasAiRows ? (
      <p className="text-muted mb-0 mt-2" style={{ fontSize: "0.8rem" }}>
        DigiKey verified each exact MPN, lifecycle, stock and price. AI compatibility
        recommendations still require engineering review. The selection is rechecked on save.
      </p>
    ) : null}
    </>
  );
};

/**
 * Uses substitutes already attached to bom lines from digikey_enrichment.
 * No-alt rows can fetch AI suggestions; after that they join the same radio table / Save.
 */
const ObsoletePartsReviewModal = ({
  show,
  bomlines,
  focusLineKey,
  token,
  onSave,
  onClose,
  setLoading,
  setLoadingMessage,
  setLoadingPercent,
}) => {
  const obsoleteLines = useMemo(
    () => collectObsoleteBomLines(bomlines || []),
    [bomlines],
  );

  const [expandedKey, setExpandedKey] = useState("");
  const [expandedNoAltKey, setExpandedNoAltKey] = useState("");
  const [selections, setSelections] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState(0);
  const [saveError, setSaveError] = useState("");
  const simTimerRef = useRef(null);
  const aiLoaderTimerRef = useRef(null);
  const accordionItemRefs = useRef({});
  const modalBoxRef = useRef(null);
  const initializedOpenRef = useRef(false);
  const didAutoScrollRef = useRef(false);
  const userHasScrolledRef = useRef(false);
  const [scrollToKey, setScrollToKey] = useState("");
  const prefetchedAiRef = useRef({});

  // Clear progress timers on unmount
  useEffect(() => () => {
    if (simTimerRef.current) clearInterval(simTimerRef.current);
    if (aiLoaderTimerRef.current) clearInterval(aiLoaderTimerRef.current);
  }, []);

  // Per-MPN AI results for this modal session (DigiKey-empty parts only)
  const [suggestionsMap, setSuggestionsMap] = useState({});
  const [suggestionsLoadingMap, setSuggestionsLoadingMap] = useState({});
  const [suggestionsErrorMap, setSuggestionsErrorMap] = useState({});
  // Tracks whether each MPN's suggestions came from DB cache + when they were last refreshed.
  const [suggestionsCacheMap, setSuggestionsCacheMap] = useState({});

  // DigiKey/saved rows win on the same MPN; cached AI rows fill in the rest.
  const substitutesForLine = useCallback((line) => {
    const fromLine = prependOriginalObsoleteAlternate(
      substitutesFromLine(line),
      line,
      line,
    );
    const mpn = lineMpn(line);
    const suggested = mpn && Array.isArray(suggestionsMap[mpn])
      ? suggestionsMap[mpn]
      : [];
    if (!suggested.length) {
      return overlaySelectedDigiKeyFields(line, fromLine);
    }
    if (!fromLine.length) {
      return overlaySelectedDigiKeyFields(line, suggested);
    }
    const seen = {};
    const merged = [];
    fromLine.forEach((item) => {
      const key = substituteMpn(item).toUpperCase();
      if (key) seen[key] = merged.length;
      merged.push(item);
    });
    suggested.forEach((item) => {
      const key = substituteMpn(item).toUpperCase();
      if (key && Object.prototype.hasOwnProperty.call(seen, key)) {
        const index = seen[key];
        merged[index] = mergeAiRowIdentity(merged[index], item);
        return;
      }
      if (key) seen[key] = merged.length;
      merged.push(item);
    });
    return overlaySelectedDigiKeyFields(line, merged);
  }, [suggestionsMap]);

  const { withAlternates, noAlternates } = useMemo(() => {
    const withAlts = [];
    const noAlts = [];
    obsoleteLines.forEach((line) => {
      if (substitutesForLine(line).length > 0) {
        withAlts.push(line);
      } else {
        noAlts.push(line);
      }
    });
    return { withAlternates: withAlts, noAlternates: noAlts };
  }, [obsoleteLines, substitutesForLine]);

  // AI Suggest / Refresh. force=true skips the in-memory map and asks the API to refresh.
  const fetchSuggestions = useCallback(async (line, force = false, silent = false) => {
    const mpn = lineMpn(line);
    const key = getObsoleteLineKey(line);
    if (!mpn || suggestionsLoadingMap[mpn]) {
      return;
    }
    if (!force && Object.prototype.hasOwnProperty.call(suggestionsMap, mpn)) {
      return;
    }

    const stopAiLoader = () => {
      if (aiLoaderTimerRef.current) {
        clearInterval(aiLoaderTimerRef.current);
        aiLoaderTimerRef.current = null;
      }
      if (typeof setLoading === "function") {
        setLoading(false);
      }
      if (typeof setLoadingPercent === "function") {
        setLoadingPercent(0);
      }
      if (typeof setLoadingMessage === "function") {
        setLoadingMessage("");
      }
    };

    setSuggestionsErrorMap((prev) => ({ ...prev, [mpn]: null }));
    if (silent) {
      setSuggestionsLoadingMap((prev) => ({ ...prev, [mpn]: true }));
    } else {
      flushSync(() => {
        setSuggestionsLoadingMap((prev) => ({ ...prev, [mpn]: true }));
        if (typeof setLoading === "function") {
          setLoading(true);
        }
        if (typeof setLoadingMessage === "function") {
          setLoadingMessage("Finding AI suggestions and checking DigiKey…");
        }
        if (typeof setLoadingPercent === "function") {
          setLoadingPercent(12);
        }
      });

      let simPct = 12;
      aiLoaderTimerRef.current = setInterval(() => {
        simPct = Math.min(simPct + 4, 90);
        if (typeof setLoadingPercent === "function") {
          setLoadingPercent(Math.round(simPct));
        }
      }, 400);
    }

    try {
      const res = await fetch(DIGIKEY_AI_ALTERNATES, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          part_number: mpn,
          refresh: !!force,
        }),
      });
      const json = await res.json();
      if (!res.ok || !(json?.code >= 200 && json?.code < 400)) {
        throw new Error(json?.message || `Error ${res.status}`);
      }
      const items = Array.isArray(json?.data?.substitutes)
        ? json.data.substitutes
        : [];
      const rejected = Array.isArray(json?.data?.rejected)
        ? json.data.rejected
        : [];
      const rejectedCount = Number(json?.data?.rejected_count || rejected.length || 0);
      const message = String(json?.data?.message || "").trim();
      setSuggestionsMap((prev) => ({ ...prev, [mpn]: items }));
      // Track cache + empty-result reasons so Refresh and the status copy stay in sync.
      setSuggestionsCacheMap((prev) => ({
        ...prev,
        [mpn]: {
          fromCache: !!json.data?.from_cache,
          cachedAt: json.data?.cached_at ?? null,
          message,
          rejected,
          rejectedCount,
        },
      }));
      if (!silent) {
        if (items.length > 0) {
          const preferred = sortByUnitPrice(items)[0];
          const preferredMpn = substituteMpn(preferred);
          if (preferredMpn) {
            setSelections((prev) => ({ ...prev, [key]: preferredMpn }));
          }
          setExpandedKey(key);
          setExpandedNoAltKey("");
          setScrollToKey(key);
        } else {
          setExpandedNoAltKey(key);
          setExpandedKey("");
        }
      }
    } catch (err) {
      setSuggestionsErrorMap((prev) => ({
        ...prev,
        [mpn]: err.message || "Failed to fetch AI suggestions",
      }));
      if (!silent) {
        setExpandedNoAltKey(key);
        setExpandedKey("");
      }
    } finally {
      if (!silent) {
        stopAiLoader();
      }
      setSuggestionsLoadingMap((prev) => ({ ...prev, [mpn]: false }));
    }
  }, [
    suggestionsMap,
    suggestionsLoadingMap,
    token,
    setLoading,
    setLoadingMessage,
    setLoadingPercent,
  ]);

  useEffect(() => {
    if (!show) {
      initializedOpenRef.current = false;
      didAutoScrollRef.current = false;
      userHasScrolledRef.current = false;
      prefetchedAiRef.current = {};
      setScrollToKey("");
      return;
    }
    if (initializedOpenRef.current || !obsoleteLines.length) {
      return;
    }
    initializedOpenRef.current = true;

    const initialSelections = {};

    obsoleteLines.forEach((line) => {
      const key = getObsoleteLineKey(line);
      const substitutes = prependOriginalObsoleteAlternate(
        substitutesFromLine(line),
        line,
        line,
      );
      const sessionSelected = String(line.selected_alternate_mpn || "").trim();
      if (sessionSelected) {
        initialSelections[key] = sessionSelected;
        return;
      }
      const recommendedMpn = String(line.preselect_alternate_mpn || "").trim();
      const recommendedInList = recommendedMpn
        ? substitutes.some(
            (item) =>
              substituteMpn(item).toUpperCase() === recommendedMpn.toUpperCase(),
          )
        : false;
      if (recommendedInList) {
        initialSelections[key] = substituteMpn(
          substitutes.find(
            (item) =>
              substituteMpn(item).toUpperCase() === recommendedMpn.toUpperCase(),
          ),
        );
        return;
      }
      const sorted = sortByUnitPrice(substitutes);
      if (sorted.length > 0) {
        const firstMpn = substituteMpn(sorted[0]);
        if (firstMpn) {
          initialSelections[key] = firstMpn;
        }
      }
    });

    setSelections(initialSelections);
    setSaveError("");
    const seedSuggestions = {};
    const seedCache = {};
    obsoleteLines.forEach((line) => {
      const mpn = lineMpn(line);
      if (!mpn || line.ai_suggestions_attempted !== true) {
        return;
      }
      seedSuggestions[mpn] = Array.isArray(line.ai_suggestions)
        ? line.ai_suggestions
        : [];
      seedCache[mpn] = {
        fromCache: true,
        cachedAt: line.ai_suggestions_at || null,
        message: line.ai_suggestions_message || "",
        rejected: Array.isArray(line.ai_suggestions_rejected)
          ? line.ai_suggestions_rejected
          : [],
        rejectedCount: Array.isArray(line.ai_suggestions_rejected)
          ? line.ai_suggestions_rejected.length
          : 0,
      };
    });
    setSuggestionsMap(seedSuggestions);
    setSuggestionsLoadingMap({});
    setSuggestionsErrorMap({});
    setSuggestionsCacheMap(seedCache);

    const focusInNoAlt =
      !!focusLineKey &&
      noAlternates.some((line) => getObsoleteLineKey(line) === focusLineKey);
    if (focusInNoAlt) {
      setExpandedNoAltKey(focusLineKey);
      setExpandedKey("");
    } else {
      setExpandedKey(
        focusLineKey ||
          (withAlternates[0] ? getObsoleteLineKey(withAlternates[0]) : ""),
      );
      setExpandedNoAltKey("");
    }
  }, [show, obsoleteLines, focusLineKey, withAlternates, noAlternates]);

  // Reopen: load cached AI list so unselected suggestions come back.
  useEffect(() => {
    if (!show || !initializedOpenRef.current) {
      return;
    }
    obsoleteLines.forEach((line) => {
      const fromLine = substitutesFromLine(line);
      const group = String(line.selected_alternate_group || "").toLowerCase();
      const type = String(line.selected_alternate_type || "").toLowerCase();
      const isAiLine =
        group === "ai" ||
        type.indexOf("ai") !== -1 ||
        fromLine.some(isAiAlternate);
      if (!isAiLine) {
        return;
      }
      const mpn = lineMpn(line);
      if (!mpn || prefetchedAiRef.current[mpn]) {
        return;
      }
      // Re-import already restored the cached AI list / empty attempt.
      if (
        line.ai_suggestions_attempted === true ||
        (Array.isArray(line.ai_suggestions) && line.ai_suggestions.length > 0)
      ) {
        prefetchedAiRef.current[mpn] = true;
        return;
      }
      prefetchedAiRef.current[mpn] = true;
      fetchSuggestions(line, false, true);
    });
  }, [show, obsoleteLines, fetchSuggestions]);

  // Scroll to a specific BOM line once when the modal opens. Do not repeat after
  // the user scrolls, expands another accordion, or picks a different alternate.
  useEffect(() => {
    if (!show || !focusLineKey || didAutoScrollRef.current) return;
    const isFocusedOpen =
      expandedKey === focusLineKey || expandedNoAltKey === focusLineKey;
    if (!isFocusedOpen) return;

    const firstWithAltKey = withAlternates[0]
      ? getObsoleteLineKey(withAlternates[0])
      : "";
    const firstNoAltKey = noAlternates[0]
      ? getObsoleteLineKey(noAlternates[0])
      : "";
    const isFirstVisibleItem =
      focusLineKey === firstWithAltKey ||
      (!firstWithAltKey && focusLineKey === firstNoAltKey);
    if (isFirstVisibleItem) {
      didAutoScrollRef.current = true;
      return;
    }

    const scrollFocusedItemExact = () => {
      if (userHasScrolledRef.current || didAutoScrollRef.current) return;
      const modal = modalBoxRef.current;
      const itemEl = accordionItemRefs.current[focusLineKey];
      if (!modal || !itemEl) return;

      const header = itemEl.querySelector(".obsolete-accordion-header");
      const modalRect = modal.getBoundingClientRect();
      const targetRect = (header || itemEl).getBoundingClientRect();
      const nextTop =
        modal.scrollTop + (targetRect.top - modalRect.top) - 12;

      modal.scrollTo({
        top: Math.max(0, nextTop),
        behavior: "auto",
      });
      didAutoScrollRef.current = true;
    };

    const t1 = setTimeout(scrollFocusedItemExact, 80);
    return () => {
      clearTimeout(t1);
    };
  }, [show, expandedKey, expandedNoAltKey, focusLineKey, withAlternates, noAlternates]);

  // After AI results land, the row jumps to the top list — scroll it into view.
  useEffect(() => {
    if (!show || !scrollToKey) return;
    if (expandedKey !== scrollToKey) return;

    let tries = 0;
    let timer = null;
    const attempt = () => {
      const modal = modalBoxRef.current;
      const itemEl = accordionItemRefs.current[scrollToKey];
      if (modal && itemEl) {
        const header = itemEl.querySelector(".obsolete-accordion-header");
        const modalRect = modal.getBoundingClientRect();
        const targetRect = (header || itemEl).getBoundingClientRect();
        const nextTop =
          modal.scrollTop + (targetRect.top - modalRect.top) - 12;
        modal.scrollTo({
          top: Math.max(0, nextTop),
          behavior: "smooth",
        });
        setScrollToKey("");
        return;
      }
      tries += 1;
      if (tries < 12) {
        timer = setTimeout(attempt, 50);
        return;
      }
      setScrollToKey("");
    };

    timer = setTimeout(attempt, 80);
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [show, scrollToKey, expandedKey, withAlternates]);

  const toggleAccordion = (line) => {
    const key = getObsoleteLineKey(line);
    setExpandedKey((prev) => (prev === key ? "" : key));
  };

  const toggleNoAltAccordion = (line) => {
    const key = getObsoleteLineKey(line);
    setExpandedNoAltKey((prev) => (prev === key ? "" : key));
  };

  const findAlternateByMpn = (line, selectedMpn) => {
    const target = String(selectedMpn || "").trim().toUpperCase();
    if (!target) return null;
    return substitutesForLine(line).find((item) => {
      const mpn = String(
        item.manufacturer_part_number || item.manufacturerPartNumber || "",
      )
        .trim()
        .toUpperCase();
      return mpn === target;
    }) || null;
  };

  const handleSave = async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (saving) return;

    // Validate: every line that has alternatives must have a selection
    const unselected = withAlternates.filter((line) => {
      const key = getObsoleteLineKey(line);
      return !selections[key];
    });

    if (unselected.length > 0) {
      setSaveError(
        "Some alternatives are not selected. Please select all required alternatives before saving.",
      );
      setExpandedKey(getObsoleteLineKey(unselected[0]));
      return;
    }

    const payload = withAlternates
      .map((line) => {
        const key = getObsoleteLineKey(line);
        const selectedMpn = selections[key];
        if (!selectedMpn) return null;
        const alternate = findAlternateByMpn(line, selectedMpn);
        if (!alternate) return null;
        return {
          line: { ...line, substitutes: substitutesForLine(line) },
          alternate,
        };
      })
      .filter(Boolean);

    if (!payload.length) {
      setSaveError(
        "Could not resolve selected alternates. Please re-select each alternate and try again.",
      );
      return;
    }

    setSaveError("");
    const partCount = payload.length;

    const updateProgress = (pct, label) => {
      setSaveProgress(pct);
      if (typeof setLoadingPercent === "function") {
        setLoadingPercent(pct);
      }
      if (typeof setLoadingMessage === "function") {
        setLoadingMessage(label);
      }
    };

    // Force paint, then show the global OverlayLoader (z-index 9999) above the modal.
    flushSync(() => {
      setSaving(true);
      updateProgress(8, "Saving selections…");
      if (typeof setLoading === "function") {
        setLoading(true);
      }
    });

    let simPct = 8;
    simTimerRef.current = setInterval(() => {
      simPct = Math.min(simPct + 1.5, 94);
      const rounded = Math.round(simPct);
      let label = "Saving selections…";
      if (simPct >= 25 && simPct < 55) {
        label = `Fetching DigiKey data for ${partCount} selected part${partCount > 1 ? "s" : ""}…`;
      } else if (simPct >= 55 && simPct < 80) {
        label = "Enriching part details (datasheet, country of origin)…";
      } else if (simPct >= 80) {
        label = "Almost done, finishing up…";
      }
      updateProgress(rounded, label);
    }, 450);

    try {
      await onSave(payload);
      clearInterval(simTimerRef.current);
      simTimerRef.current = null;
      flushSync(() => {
        updateProgress(100, "Done!");
      });
      await new Promise((r) => setTimeout(r, 600));
      onClose();
    } catch (err) {
      clearInterval(simTimerRef.current);
      simTimerRef.current = null;
      setSaveError(
        err?.message || "Failed to save obsolete alternates. Please try again.",
      );
    } finally {
      setSaving(false);
      setSaveProgress(0);
      if (typeof setLoading === "function") {
        setLoading(false);
      }
      if (typeof setLoadingPercent === "function") {
        setLoadingPercent(0);
      }
      if (typeof setLoadingMessage === "function") {
        setLoadingMessage("");
      }
    }
  };

  if (!show || !obsoleteLines.length) {
    return null;
  }

  const selectedCount = withAlternates.filter((line) =>
    selections[getObsoleteLineKey(line)],
  ).length;

  return (
    <ModalOverlay className={classnames({ show })}>
      <ModalBox
        ref={modalBoxRef}
        className="theme-form obsolete-parts-review-modal"
        onScroll={() => {
          userHasScrolledRef.current = true;
        }}
        style={{
          maxWidth: "1600px",
          width: "calc(100vw - 48px)",
          maxHeight: "90vh",
          overflowX: "hidden",
          overflowY: "auto",
          position: "relative",
        }}
      >
        <div className="obsolete-parts-modal-header">
          <h5 className="mb-0">Obsolete Parts – Select Alternates</h5>
          <button
            type="button"
            className="obsolete-parts-modal-close"
            onClick={onClose}
            aria-label="Close"
            disabled={saving}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <p className="text-muted mb-3">
          {obsoleteLines.length} obsolete part(s) found. Expand each item to
          review substitutes and alternates, then save your selections.
        </p>

        {/* Parts with alternatives */}
        {withAlternates.length > 0 && (
          <div className="obsolete-parts-accordion mb-3">
            {withAlternates.map((line) => {
              const key = getObsoleteLineKey(line);
              const obsoleteMpn =
                line.obsolete_part_number || line.manufacturer_part_number;
              const substitutes = substitutesForLine(line);
              const isOpen = expandedKey === key;
              const selectedMpn = selections[key] || "";

              const selectedAlternate = selectedMpn
                ? findAlternateByMpn(line, selectedMpn)
                : null;
            const selectedMfr = selectedAlternate?.manufacturer
              ? String(selectedAlternate.manufacturer).trim()
              : "";

            return (
                <div
                  key={key}
                  className={classnames("obsolete-accordion-item mb-2", {
                    "is-focused":
                      focusLineKey === key ||
                      (expandedKey === key &&
                        !!suggestionsMap[lineMpn(line)]),
                  })}
                  ref={(node) => {
                    if (node) {
                      accordionItemRefs.current[key] = node;
                    } else {
                      delete accordionItemRefs.current[key];
                    }
                  }}
                >
                  <button
                    type="button"
                    className={classnames(
                      "obsolete-accordion-header w-100 d-flex align-items-center justify-content-between",
                      { open: isOpen },
                    )}
                    onClick={() => toggleAccordion(line)}
                  >
                    <span>
                      <strong>{obsoleteMpn}</strong>
                      {line.manufacturer ? (
                        <span className="text-muted ml-2">
                          {line.manufacturer}
                        </span>
                      ) : null}
                    </span>
                    <span className="d-flex align-items-center">
                      {selectedMpn ? (
                        <Badge color="success" className="mr-2">
                          Selected: {selectedMpn}
                          {selectedMfr ? ` / ${selectedMfr}` : ""}
                        </Badge>
                      ) : null}
                      {substitutes.some(isOriginalObsoleteAlternate) ? (
                        <Badge color="warning" className="mr-2">
                          Obsolete but in stock
                        </Badge>
                      ) : null}
                      <Badge color="warning">{substitutes.length}</Badge>
                    </span>
                  </button>
                  <Collapse isOpen={isOpen}>
                    <div className="obsolete-accordion-body p-3">
                      <ObsoleteAlternateTable
                        substitutes={substitutes}
                        selectedMpn={selectedMpn}
                        radioName={`obsolete-alt-${key}`}
                        onSelect={(mpn) => {
                          setSaveError("");
                          setSelections((prev) => ({ ...prev, [key]: mpn }));
                        }}
                      />
                      {/* AI disclaimer + cache/refresh footer — always shown for AI-sourced suggestions */}
                      {suggestionsCacheMap[lineMpn(line)] && (
                        <div className="mt-2 pt-2" style={{ borderTop: "1px solid #f0f0f0" }}>
                          {/* AI generated notice */}
                          <div
                            className="d-flex align-items-center mb-1"
                            style={{ fontSize: "0.74rem", color: "#856404", background: "#fff9e6", border: "1px solid #ffeeba", borderRadius: "4px", padding: "3px 8px", gap: "5px" }}
                          >
                            <span>🤖</span>
                            <span>
                              AI generated — specs may not be fully accurate. Only parts DigiKey confirmed are listed; pricing and stock come from DigiKey.
                            </span>
                          </div>
                          {suggestionsCacheMap[lineMpn(line)].message ? (
                            <div className="mb-1" style={{ fontSize: "0.74rem", color: "#6c757d" }}>
                              {suggestionsCacheMap[lineMpn(line)].message}
                              <AiRejectedReasons rejected={suggestionsCacheMap[lineMpn(line)].rejected} />
                            </div>
                          ) : null}
                          {/* Cache row with Regenerate — same Suggest path, refresh=true */}
                          <div className="d-flex align-items-center justify-content-between" style={{ fontSize: "0.74rem", color: "#6c757d" }}>
                            <span>
                              {suggestionsCacheMap[lineMpn(line)].fromCache
                                ? `💾 Loaded from cache · ${relativeTime(suggestionsCacheMap[lineMpn(line)].cachedAt)}`
                                : "✨ Fresh AI results"}
                            </span>
                            <Button
                              type="button"
                              className="importBtn px-2 w-auto"
                              style={{ fontSize: "0.74rem", background: suggestionsCacheMap[lineMpn(line)].fromCache ? "#0d6efd" : "transparent", color: suggestionsCacheMap[lineMpn(line)].fromCache ? "#fff" : "#6c757d", border: "1px solid #ced4da", padding: "2px 10px" }}
                              disabled={!!suggestionsLoadingMap[lineMpn(line)]}
                              onClick={() => fetchSuggestions(line, true)}
                            >
                              {suggestionsLoadingMap[lineMpn(line)] ? "Fetching…" : (suggestionsCacheMap[lineMpn(line)].fromCache ? "🔄 Regenerate" : "Refresh")}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </Collapse>
                </div>
              );
            })}
          </div>
        )}

        {/* DigiKey found nothing — user can ask AI for MPNs */}
        {noAlternates.length > 0 && (
          <div className="mb-3">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h6 className="text-muted mb-0" style={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                No Alternates Found ({noAlternates.length})
              </h6>
            </div>
            <div className="obsolete-parts-accordion">
              {noAlternates.map((line) => {
                const key = getObsoleteLineKey(line);
                const obsoleteMpn =
                  line.obsolete_part_number || line.manufacturer_part_number;
                const isOpen = expandedNoAltKey === key;
                const alreadyTried = Object.prototype.hasOwnProperty.call(
                  suggestionsMap,
                  obsoleteMpn,
                );
                const suggestMeta = suggestionsCacheMap[obsoleteMpn] || {};
                const digiKeySearchUrl = `https://www.digikey.com/en/products/filter?keywords=${encodeURIComponent(obsoleteMpn || "")}`;

                return (
                  <div
                    key={key}
                    className={classnames("obsolete-accordion-item mb-2", {
                      "is-focused": focusLineKey === key,
                    })}
                    ref={(node) => {
                      if (node) {
                        accordionItemRefs.current[key] = node;
                      } else {
                        delete accordionItemRefs.current[key];
                      }
                    }}
                  >
                    {/* Header: part name + inline AI Suggest button + badge */}
                    <div
                      className={classnames(
                        "obsolete-accordion-header w-100 d-flex align-items-center justify-content-between",
                        { open: isOpen },
                      )}
                      onClick={() => toggleNoAltAccordion(line)}
                      style={{ cursor: "pointer" }}
                    >
                      <span className="d-flex align-items-center flex-wrap" style={{ gap: "6px" }}>
                        <strong>{obsoleteMpn}</strong>
                        {line.manufacturer ? (
                          <span className="text-muted">{line.manufacturer}</span>
                        ) : null}
                        {/* Inline AI Suggest button — click loads suggestions without opening accordion */}
                        <button
                          type="button"
                          className="importBtn blue px-2 w-auto"
                          style={{ fontSize: "0.72rem", padding: "2px 8px", lineHeight: "1.4" }}
                          disabled={!!suggestionsLoadingMap[obsoleteMpn]}
                          onClick={(e) => {
                            e.stopPropagation();
                            fetchSuggestions(line, alreadyTried);
                          }}
                        >
                          {suggestionsLoadingMap[obsoleteMpn]
                            ? "Finding…"
                            : alreadyTried
                              ? "🔄 Refresh"
                              : "✨ AI Suggest"}
                        </button>
                        {alreadyTried && suggestMeta.message ? (
                          <span className="text-muted" style={{ fontSize: "0.75rem" }}>
                            {suggestMeta.message}
                          </span>
                        ) : null}
                      </span>
                      <Badge color="secondary">0 Alternates</Badge>
                    </div>
                    <Collapse isOpen={isOpen}>
                      <div className="obsolete-accordion-body p-3">
                        {/* No unfetched state message — the header button makes intent clear */}

                        {suggestionsErrorMap[obsoleteMpn] && (
                          <div className="d-flex align-items-center flex-wrap" style={{ gap: "8px" }}>
                            <span className="text-muted" style={{ fontSize: "0.875rem" }}>
                              Could not load AI suggestions. Refresh to try the same DigiKey lookup again.
                            </span>
                            <a
                              href={digiKeySearchUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Button
                                type="button"
                                className="importBtn blue px-3 w-auto"
                                style={{ fontSize: "0.8rem" }}
                              >
                                Search on DigiKey <ExternalLinkIcon />
                              </Button>
                            </a>
                            <Button
                              type="button"
                              className="importBtn px-3 w-auto"
                              style={{ fontSize: "0.8rem", background: "transparent", color: "#6c757d", border: "1px solid #ced4da" }}
                              disabled={!!suggestionsLoadingMap[obsoleteMpn]}
                              onClick={() => fetchSuggestions(line, true)}
                            >
                              {suggestionsLoadingMap[obsoleteMpn] ? "Fetching…" : "Refresh"}
                            </Button>
                          </div>
                        )}

                        {Array.isArray(suggestionsMap[obsoleteMpn]) &&
                          suggestionsMap[obsoleteMpn].length === 0 &&
                          !suggestionsLoadingMap[obsoleteMpn] &&
                          !suggestionsErrorMap[obsoleteMpn] && (
                          <div>
                            <div
                              style={{
                                fontSize: "0.875rem",
                                color: "#842029",
                                background: "#fff3f3",
                                border: "1px solid #f5c6cb",
                                borderRadius: "4px",
                                padding: "8px 10px",
                              }}
                            >
                              {suggestMeta.message ||
                                "No DigiKey-confirmed alternates are available for this part."}
                              <AiRejectedReasons rejected={suggestMeta.rejected} />
                            </div>
                            <div className="d-flex align-items-center flex-wrap mt-2" style={{ gap: "8px" }}>
                              <a
                                href={digiKeySearchUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Button
                                  type="button"
                                  className="importBtn blue px-3 w-auto"
                                  style={{ fontSize: "0.8rem" }}
                                >
                                  Search on DigiKey <ExternalLinkIcon />
                                </Button>
                              </a>
                              <Button
                                type="button"
                                className="importBtn px-3 w-auto"
                                style={{ fontSize: "0.8rem", background: "transparent", color: "#6c757d", border: "1px solid #ced4da" }}
                                disabled={!!suggestionsLoadingMap[obsoleteMpn]}
                                onClick={() => fetchSuggestions(line, true)}
                              >
                                {suggestionsLoadingMap[obsoleteMpn] ? "Fetching…" : "Refresh"}
                              </Button>
                            </div>
                            {suggestMeta.fromCache ? (
                              <div className="mt-2" style={{ fontSize: "0.74rem", color: "#6c757d" }}>
                                Loaded from cache · {relativeTime(suggestMeta.cachedAt)}
                              </div>
                            ) : alreadyTried ? (
                              <div className="mt-2" style={{ fontSize: "0.74rem", color: "#6c757d" }}>
                                Fresh AI results — Refresh runs the same DigiKey-confirmed lookup again.
                              </div>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </Collapse>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {saveError && (
          <div
            className="mb-3 px-3 py-2"
            style={{
              backgroundColor: "#fff3f3",
              border: "1px solid #f5c6cb",
              borderRadius: "4px",
              color: "#842029",
              fontSize: "0.875rem",
            }}
          >
            {saveError}
          </div>
        )}

        <div className="obsolete-parts-modal-footer d-flex justify-content-between align-items-center">
          <span className="text-muted small">
            {selectedCount} of {withAlternates.length} part(s) selected
          </span>
          <div className="obsolete-parts-modal-actions d-flex align-items-center">
            <Button
              type="button"
              className="importBtn blue px-4 w-auto mr-2"
              disabled={saving}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="importBtn blue px-4 w-auto"
              disabled={saving || withAlternates.length === 0}
              onClick={handleSave}
            >
              {saving ? `Saving ${saveProgress}%` : "Save Selections"}
            </Button>
          </div>
        </div>
      </ModalBox>
    </ModalOverlay>
  );
};

export default ObsoletePartsReviewModal;
